import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import FormSection from 'Components/Molecules/Form/FormSection/FormSection';
import Field from 'Pages/Dashboard/Settings/FieldsManagement/Fields/FieldTypes/Field';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { getInternalDatasetIdByName } from 'Services/helpers';
import { RelationService } from 'Services/Relation/Application/RelationService';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import { saveBusinessUnitEntity } from 'Store/BusinessUnit/Actions';
import { getBusinessUnitById, getBusinessUnitDatasetKeys, isBusinessUnitDatasetFetching } from 'Store/BusinessUnit/Selectors';
import { saveCandidateEntity } from 'Store/Candidate/Actions';
import { getCandidateById, getCandidateDatasetKeys, isCandidateDatasetFetching } from 'Store/Candidate/Selectors';
import { updateContact } from 'Store/Contact/Actions';
import { getContactById, getContactDatasetKeys, isContactDatasetFetching } from 'Store/Contact/Selectors';
import { fetchAllDatasets, saveRelation } from 'Store/FieldManagement/Actions';
import { FieldType } from 'Store/FieldManagement/PropTypes';
import { StoreState } from 'Store/Reducers';
import { getEntitiesById } from 'Store/Settings/Entities/Selectors';
import { saveVacancyEntity } from 'Store/Vacancy/Actions';
import { getVacancyById, getVacancyDatasetKeys, isVacancyDatasetFetching } from 'Store/Vacancy/Selectors';

const EntityRelationStep = ({
    entitySetting, record_id,

    handleValidation,

    entityById,

    vacancy, candidate, contact, business_unit,
    vacancyDatasetKeys, candidateDatasetKeys, contactDatasetKeys, businessUnitDatasetKeys,
    isVacancyDatasetFetching, isCandidateDatasetFetching, isContactDatasetFetching, isBusinessUnitFetching,

    ...props
}: EntityRelationStepProps) => {
    useEffect(() => {
        props.fetchAllDatasets();
    }, []);

    const [state, setState] = useState({
        isEdit: false,
        isRelationFetching: false,
        isPreviewLoading: true,
        isActive: false
    });

    const initialData = useMemo(() => ({
        ...vacancy?.relations,
        ...candidate?.relations,
        ...contact?.relations,
        ...business_unit?.relations
    }), [vacancy?.relations, candidate?.relations, contact?.relations, business_unit?.relations]);
    const [data, setData] = useState(initialData);

    useEffect(() => {
        checkValidation(initialData);
    }, [initialData]);

    const checkValidation = (values?: any) => handleValidation(relationEditables.every(i => {
        if (entitySetting.name === 'contact' && entityById?.[i.id]?.name === 'vacancy') return true;
        if (entitySetting.name === 'vacancy' && entityById?.[i.id]?.name === 'contact') return true;

        return (values || data)?.[i.id]?.length > 0
    }));

    const relationPreviews = useMemo(() => (entitySetting.relations || [])
        .filter(i => i.isActive && !i.isEditable)
        .sort((a, b) => a.order - b.order)
        , [entitySetting.relations]);

    const relationEditables = useMemo(() => (entitySetting.relations || [])
        .filter(i => i.isActive && i.isEditable)
        .sort((a, b) => a.order - b.order)
        , [entitySetting.relations]);

    const handleRelationChange = async (currentIteration, maxIteration, values) => {
        const id = relationEditables?.[currentIteration]?.id;
        const aboveRelationValues = values?.[id];

        const underFieldId = relationEditables?.[currentIteration + 1]?.id;
        const underFieldValues = [...(values?.[underFieldId] || [])];

        let newUnderFieldValues: any = [];
        if (aboveRelationValues && aboveRelationValues?.length > 0) {
            const relationService = new RelationService();
            const relationValues = await relationService.fetchRelations(aboveRelationValues);

            newUnderFieldValues = underFieldValues.filter(i => relationValues.includes(i));
        }

        if (underFieldId) {
            values = {
                ...values,
                [underFieldId]: newUnderFieldValues
            }
        }

        if (currentIteration >= maxIteration) {
            setState({ ...state, isRelationFetching: false });
            setData(values);

            return;
        }

        handleRelationChange(currentIteration + 1, maxIteration, { ...values });
    }

    const handleChange = (idx, key, value) => {
        const newValues = {
            ...data,
            [key]: value
        };

        const fieldLength = relationEditables.length - 1;
        const nextFieldId = relationEditables?.[idx + 1]?.id;

        const isLastItem = idx >= fieldLength;

        if (!isLastItem && (nextFieldId && data?.[nextFieldId]?.length > 0)) {
            setState({
                ...state,
                isRelationFetching: true
            });

            handleRelationChange(idx, fieldLength, newValues);

            return;
        }

        checkValidation(newValues);
        setData(newValues);
        handleSave(newValues);
    }

    const handleSave = (data: any) => {
        let isChanged = false;
        for (const key of Object.keys(data)) {
            if (isChanged) continue;

            if (data?.[key]?.length != initialData?.[key]?.length) {
                isChanged = true;
                continue;
            }

            for (const idx in data?.[key]) {
                if (data[key][idx] == initialData[key][idx]) continue;

                isChanged = true;
            }
        }

        if (!isChanged) return;

        switch (entitySetting.name) {
            case 'vacancy':
                props.saveVacancyEntity(record_id, { relations: data });
                break;
            case 'candidate':
                props.saveCandidateEntity(record_id, { relations: data });
                break;
            case 'contact':
                props.updateContact(record_id, { relations: data });
                break;
            case 'business_unit':
                props.saveBusinessUnitEntity(record_id, { relations: data });
                break;
            default:
                break;
        }
        const relations = relationEditables.reduce((a, c) => a = [...(a || []), ...(data?.[c.id] || [])], []);
        props.saveRelation({
            dataset_key: record_id,
            relations: relations
        });
    };

    return <FormSection>
        {
            relationEditables.length > 0 && relationEditables.map((i, idx) => {
                const aboveFieldId = relationEditables?.[idx - 1]?.id;
                const isDisabled = !aboveFieldId ? false : !(data?.[aboveFieldId] && data?.[aboveFieldId]?.length > 0);

                const relation = aboveFieldId ? data?.[aboveFieldId] : [];

                const isRequired = !(entitySetting.name === 'contact' && entityById?.[i.id]?.name === 'vacancy')
                    && !(entitySetting.name === 'vacancy' && entityById?.[i.id]?.name === 'contact');

                return <FormGroup title={EntityHelper.getDisplayValueByName(entityById?.[i.id]?.name)} isRequired={isRequired}>
                    <Field
                        isEdit={true}
                        type={FieldType.Multi_Selector}
                        value={data?.[i.id] || []}
                        onChange={(value) => handleChange(idx, i.id, value)}
                        fieldProperties={{
                            alignLeft: true,
                            dataset: [getInternalDatasetIdByName(entityById?.[i.id]?.name)],
                            isDisabled: isDisabled,
                            inRelation: relation
                        }}
                    />
                </FormGroup>;
            })
        }
    </FormSection>;
}

interface Props {
    entitySetting: any
    record_id: string
    handleValidation: any
};

const mapState = (state: StoreState, props: Props) => ({
    entityById: getEntitiesById(state),

    vacancy: getVacancyById(state, props.record_id),
    candidate: getCandidateById(state, props.record_id),
    business_unit: getBusinessUnitById(state, props.record_id),
    contact: getContactById(state, props.record_id),

    vacancyDatasetKeys: getVacancyDatasetKeys(state) || [],
    candidateDatasetKeys: getCandidateDatasetKeys(state) || [],
    contactDatasetKeys: getContactDatasetKeys(state) || [],
    businessUnitDatasetKeys: getBusinessUnitDatasetKeys(state) || [],

    isVacancyDatasetFetching: isVacancyDatasetFetching(state),
    isCandidateDatasetFetching: isCandidateDatasetFetching(state),
    isContactDatasetFetching: isContactDatasetFetching(state),
    isBusinessUnitFetching: isBusinessUnitDatasetFetching(state)
});

const mapDispatch = {
    saveVacancyEntity,
    updateContact,
    saveCandidateEntity,
    saveBusinessUnitEntity,
    saveRelation,
    fetchAllDatasets
};

type EntityRelationStepProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(EntityRelationStep);